/*
 * certificate-maker/templates.js — fonts, palettes and the ready documents.
 *
 * WHAT LIVES HERE AND WHAT DOES NOT
 * ─────────────────────────────────
 * Structure lives here; WORDS do not. A preset says "a title in Yeseva One at
 * 20 mm, letter-spaced, above a hand-written name" — it never says «ГРАМОТА».
 * The strings arrive from the page in the visitor's locale, so the same fifteen
 * presets serve all nine of them instead of being a Russian-only asset with
 * eight machine-translated copies.
 *
 * THE FONT LIST IS NOT A TASTE DECISION
 * ─────────────────────────────────────
 * Every face here is present in /data/font-coverage.json AND verified to cover
 * Cyrillic there — asked of the font's cmap, never inferred from its name (see
 * pdf-text-font.js for why that distinction has teeth: a face without the glyph
 * still reports a plausible width and prints a blank box). Anton, Bebas Neue,
 * Inter, Roboto and Permanent Marker are shipped by this site and deliberately
 * absent: they have no Cyrillic, and a certificate generator whose display face
 * cannot set a Russian name is a trap, not a choice.
 *
 * `bold: false` on a face is likewise a fact, not an omission. pdf-lib embeds a
 * variable font's DEFAULT instance and cannot apply the weight axis, so a face
 * with no static Bold file has no bold — offering one would print Regular while
 * the screen showed Bold (measured on /label-maker, 2026-08-20).
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertTemplates = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* id → the @font-face family the page declares, and whether a real static
     * Bold exists. `cat` drives the grouping in the font picker. */
    var FONTS = [
        { id: 'yeseva',     css: 'CM Yeseva One',    bold: false, cat: 'display' },
        { id: 'playfair',   css: 'CM Playfair',      bold: true,  cat: 'serif' },
        { id: 'russoone',   css: 'CM Russo One',     bold: false, cat: 'display' },
        { id: 'oswald',     css: 'CM Oswald',        bold: true,  cat: 'display' },
        { id: 'lobster',    css: 'CM Lobster',       bold: false, cat: 'script' },
        { id: 'marck',      css: 'CM Marck Script',  bold: false, cat: 'script' },
        { id: 'caveat',     css: 'CM Caveat',        bold: true,  cat: 'script' },
        { id: 'pacifico',   css: 'CM Pacifico',      bold: false, cat: 'script' },
        { id: 'montserrat', css: 'CM Montserrat',    bold: true,  cat: 'sans' },
        { id: 'comfortaa',  css: 'CM Comfortaa',     bold: true,  cat: 'sans' },
        { id: 'noto',       css: 'CM Noto Sans',     bold: true,  cat: 'sans' }
    ];
    var FONT_BY_ID = {};
    FONTS.forEach(function (f) { FONT_BY_ID[f.id] = f; });

    /* Paper is part of the palette because the ink only reads as "gold on
     * cream" against the cream. `paper: 'none'` is the overprint case: nothing
     * is laid down, the sheet already has its own colour. */
    var PALETTES = [
        { id: 'royal',    ink: '#1d2b45', accent: '#b08b3f', paper: '#fffdf7' },
        { id: 'emerald',  ink: '#12362b', accent: '#b08b3f', paper: '#fbfdf9' },
        { id: 'burgundy', ink: '#4a1220', accent: '#a8863c', paper: '#fffaf6' },
        { id: 'graphite', ink: '#22262b', accent: '#8a939d', paper: '#fbfbfc' },
        { id: 'sky',      ink: '#0f3a5e', accent: '#2f7fbf', paper: '#f8fbff' },
        { id: 'sunny',    ink: '#4a2f00', accent: '#e8a020', paper: '#fffdf2' },
        { id: 'rose',     ink: '#4a1d3d', accent: '#c0708f', paper: '#fffafc' },
        { id: 'forest',   ink: '#1f3d1c', accent: '#7a9a3c', paper: '#fbfdf7' },
        { id: 'mono',     ink: '#1a1a1a', accent: '#5c5c5c', paper: '#ffffff' },
        { id: 'ink',      ink: '#101418', accent: '#101418', paper: 'none' }
    ];
    var PAL_BY_ID = {};
    PALETTES.forEach(function (p) { PAL_BY_ID[p.id] = p; });

    /* The document kinds people actually search for, in the order the Wordstat
     * tail ranks them (грамота 13 057 / сертификат 9 902 / диплом 5 714 /
     * благодарственное письмо 4 838 per month, measured 2026-08-23). */
    var TYPES = ['gramota', 'diploma', 'certificate', 'thanks', 'svidetelstvo', 'gift'];

    /**
     * A preset is a page + a look + which blocks are on. `titleSize` and
     * friends are in millimetres of cap height budget, not points: this whole
     * pipeline is metric because paper is.
     */
    var PRESETS = [
        { id: 'gramota-classic',   type: 'gramota',      page: 'a4l', frame: 'classic',   pal: 'royal',    title: 'yeseva',     name: 'marck',      body: 'noto',       titleSize: 20, nameSize: 15, spacing: 4.5, wm: true },
        { id: 'gramota-laurel',    type: 'gramota',      page: 'a4l', frame: 'laurel',    pal: 'royal',    title: 'playfair',   name: 'playfair',   body: 'noto',       titleSize: 19, nameSize: 12, spacing: 5.5, wm: true, bold: true },
        { id: 'gramota-guilloche', type: 'gramota',      page: 'a4l', frame: 'guilloche', pal: 'emerald',  title: 'yeseva',     name: 'playfair',   body: 'noto',       titleSize: 18, nameSize: 12, spacing: 5,   wm: true },
        { id: 'gramota-sport',     type: 'gramota',      page: 'a4l', frame: 'deco',      pal: 'burgundy', title: 'oswald',     name: 'oswald',     body: 'montserrat', titleSize: 21, nameSize: 13, spacing: 6,   wm: false, caps: true, bold: true },
        { id: 'gramota-kids',      type: 'gramota',      page: 'a4l', frame: 'kids',      pal: 'sunny',    title: 'comfortaa',  name: 'comfortaa',  body: 'comfortaa',  titleSize: 18, nameSize: 13, spacing: 2,   wm: false, bold: true },
        { id: 'gramota-portrait',  type: 'gramota',      page: 'a4p', frame: 'classic',   pal: 'royal',    title: 'yeseva',     name: 'marck',      body: 'noto',       titleSize: 17, nameSize: 13, spacing: 4,   wm: true },
        { id: 'diploma-official',  type: 'diploma',      page: 'a4l', frame: 'guilloche', pal: 'royal',    title: 'playfair',   name: 'playfair',   body: 'noto',       titleSize: 18, nameSize: 12, spacing: 5,   wm: true, bold: true },
        { id: 'diploma-deco',      type: 'diploma',      page: 'a4l', frame: 'deco',      pal: 'graphite', title: 'montserrat', name: 'montserrat', body: 'montserrat', titleSize: 16, nameSize: 11, spacing: 7,   wm: false, caps: true, bold: true },
        { id: 'diploma-meander',   type: 'diploma',      page: 'a4l', frame: 'meander',   pal: 'emerald',  title: 'yeseva',     name: 'playfair',   body: 'noto',       titleSize: 17, nameSize: 12, spacing: 4.5, wm: true },
        { id: 'cert-corporate',    type: 'certificate',  page: 'a4l', frame: 'corners',   pal: 'graphite', title: 'montserrat', name: 'montserrat', body: 'montserrat', titleSize: 14, nameSize: 11, spacing: 6,   wm: false, caps: true, bold: true },
        { id: 'cert-wave',         type: 'certificate',  page: 'a4l', frame: 'wave',      pal: 'sky',      title: 'montserrat', name: 'playfair',   body: 'montserrat', titleSize: 15, nameSize: 12, spacing: 5,   wm: true, caps: true, bold: true },
        { id: 'cert-braid',        type: 'certificate',  page: 'a4l', frame: 'braid',     pal: 'burgundy', title: 'playfair',   name: 'playfair',   body: 'noto',       titleSize: 16, nameSize: 12, spacing: 4,   wm: true, bold: true },
        { id: 'thanks-scroll',     type: 'thanks',       page: 'a4p', frame: 'scroll',    pal: 'rose',     title: 'playfair',   name: 'marck',      body: 'noto',       titleSize: 13, nameSize: 12, spacing: 2.5, wm: true },
        { id: 'thanks-classic',    type: 'thanks',       page: 'a4l', frame: 'classic',   pal: 'burgundy', title: 'yeseva',     name: 'marck',      body: 'noto',       titleSize: 14, nameSize: 13, spacing: 3,   wm: true },
        { id: 'svid-meander',      type: 'svidetelstvo', page: 'a4l', frame: 'meander',   pal: 'forest',   title: 'playfair',   name: 'playfair',   body: 'noto',       titleSize: 15, nameSize: 12, spacing: 4,   wm: true, bold: true },
        { id: 'gift-braid',        type: 'gift',         page: 'a5l', frame: 'braid',     pal: 'burgundy', title: 'montserrat', name: 'playfair',   body: 'montserrat', titleSize: 10, nameSize: 8,  spacing: 3,   wm: true, caps: true, bold: true },
        { id: 'gift-deco',         type: 'gift',         page: 'a5l', frame: 'deco',      pal: 'graphite', title: 'oswald',     name: 'montserrat', body: 'montserrat', titleSize: 11, nameSize: 8,  spacing: 4,   wm: false, caps: true, bold: true },
        /* The overprint preset: no paper, no frame, no ornament — only the text
         * the bought blank does not already carry. Its own preset rather than a
         * checkbox because every default has to flip at once. */
        { id: 'overprint',         type: 'gramota',      page: 'a4l', frame: 'none',      pal: 'ink',      title: 'yeseva',     name: 'marck',      body: 'noto',       titleSize: 0,  nameSize: 14, spacing: 4,   wm: false, overprint: true }
    ];
    var PRESET_BY_ID = {};
    PRESETS.forEach(function (p) { PRESET_BY_ID[p.id] = p; });

    /**
     * makeDoc(presetId, texts, opts) → a document layout.js can build.
     *
     * `texts` carries every string; anything empty simply drops out of the flow
     * (see the block filter in layout.js), which is what lets one skeleton serve
     * a six-line school грамота and a two-line gift voucher.
     */
    function makeDoc(presetId, texts, opts) {
        var p = PRESET_BY_ID[presetId] || PRESETS[0];
        var pal = PAL_BY_ID[p.pal] || PALETTES[0];
        var t = texts || {};
        opts = opts || {};
        var portrait = p.page === 'a4p' || p.page === 'a5p';
        var k = p.page === 'a5l' || p.page === 'a5p' ? 0.72 : 1;   // A5 needs its own type scale

        var blocks = [];
        function push(b) { blocks.push(b); }

        /* «Пустой бланк»: переменные строки уступают место линейке под запись
         * от руки, всё постоянное остаётся напечатанным. Один флаг, а не
         * отдельный шаблон — иначе оформление пришлось бы держать в двух
         * местах, и они разъехались бы на первой же правке. */
        var blank = !!opts.blankLines;
        function pushVar(b, rulePct) {
            if (!blank) { push(b); return; }
            push({
                id: b.id, kind: 'rule', gapBefore: (b.gapBefore || 0) + (b.size || 6) * 0.9,
                size: 0.3, color: 'muted', rulePct: rulePct, align: 'center',
                widthPct: b.widthPct
            });
        }

        push({ id: 'org', kind: 'text', text: t.org || '', font: p.body, size: 4.4 * k, caps: true,
               letterSpacing: 0.55 * k, color: 'muted', align: 'center', maxLines: 2, tightRatio: 0.74,
               gapBefore: 0, lineHeight: 1.35, widthPct: 0.94 });

        push({ id: 'title', kind: 'text', text: t.title || '', font: p.title, size: p.titleSize * k || 18 * k,
               caps: true, letterSpacing: p.spacing * k, color: 'ink', align: 'center', bold: !!p.bold,
               maxLines: 1, minRatio: 0.5, gapBefore: 7 * k });

        push({ id: 'subtitle', kind: 'text', text: t.subtitle || '', font: p.body, size: 6.2 * k, caps: !!p.caps,
               letterSpacing: 0.8 * k, color: 'accent', align: 'center', maxLines: 1, gapBefore: 2.5 * k, bold: true });

        push({ id: 'lead', kind: 'text', text: t.lead || '', font: p.body, size: 5.2 * k, italic: true,
               color: 'muted', align: 'center', maxLines: 1, gapBefore: 6 * k });

        /* `tightRatio` on the NAME, not just on the organisation line. Measured
         * 2026-08-23: «Иванов Иван Иванович» at 110 mm in a 108 mm column broke
         * across two lines at full size, because the fitter returns as soon as
         * the line count is allowed — so a 2 % shrink, which nobody can see, lost
         * to a wrap, which everybody can. The name is the centrepiece of the
         * sheet; it goes to two lines only when one line would be genuinely
         * small. */
        pushVar({ id: 'name', kind: 'text', text: t.name || '', font: p.name, size: p.nameSize * k,
               color: 'ink', align: 'center', maxLines: 2, minRatio: 0.55, tightRatio: 0.72,
               gapBefore: 3.5 * k, lineHeight: 1.12 }, 0.62);

        pushVar({ id: 'detail', kind: 'text', text: t.detail || '', font: p.body, size: 5 * k,
               color: 'muted', align: 'center', maxLines: 2, gapBefore: 2.5 * k }, 0.44);

        pushVar({ id: 'reason', kind: 'text', text: t.reason || '', font: p.body, size: 5.8 * k,
               color: 'ink', align: 'center', maxLines: 3, minRatio: 0.78, gapBefore: 5 * k, lineHeight: 1.4, widthPct: 0.88 }, 0.78);

        push({ id: 'note', kind: 'text', text: t.note || '', font: p.body, size: 4.4 * k,
               color: 'muted', align: 'center', maxLines: 2, gapBefore: 3.5 * k, widthPct: 0.8 });

        /* The foot. Portrait pages have a narrow column, so date and signature
         * stack; landscape puts them on one line the way a real document does. */
        if (portrait) {
            push({ id: 'foot', kind: 'row', pin: 'bottom', gapBefore: 11 * k, cells: [
                { id: 'place', text: joinPlaceDate(t), xPct: 0, wPct: 1, align: 'center', size: 4.4 * k, color: 'muted', font: p.body }
            ] });
            push({ id: 'foot2', kind: 'row', gapBefore: 7 * k, cells: [
                { id: 'sign', kind: 'sign', role: t.role || '', name: t.signName || '', xPct: 0.18, wPct: 0.64, size: 4.6 * k, font: p.body }
            ] });
        } else {
            push({ id: 'foot', kind: 'row', pin: 'bottom', gapBefore: 12 * k, cells: [
                { id: 'place', text: joinPlaceDate(t), xPct: 0, wPct: 0.42, align: 'left', size: 4.5 * k, color: 'muted', font: p.body, maxLines: 2 },
                { id: 'sign', kind: 'sign', role: t.role || '', name: t.signName || '', xPct: 0.5, wPct: 0.5, size: 4.6 * k, font: p.body }
            ] });
        }

        push({ id: 'number', kind: 'text', text: t.number || '', font: p.body, size: 3.8 * k,
               color: 'muted', align: 'center', maxLines: 1, gapBefore: 5 * k });

        return {
            preset: p.id,
            page: { preset: opts.page || p.page },
            style: {
                frame: opts.frame || p.frame,
                ink: opts.ink || pal.ink,
                accent: opts.accent || pal.accent,
                paper: opts.paper || pal.paper,
                vAlign: opts.vAlign || 'center',
                pad: opts.pad == null ? (p.overprint ? 2 : 6) : opts.pad,
                frameInset: opts.frameInset,
                watermark: { on: opts.wm == null ? !!p.wm : !!opts.wm, size: 0.30, opacity: 0.15, color: 'accent' }
            },
            blocks: blocks,
            overlays: []
        };
    }

    /* «г. Москва, «15» мая 2026 г.» — one cell, because they belong to one
     * thought and the author should not have to align two boxes. */
    function joinPlaceDate(t) {
        return [t.place, t.date].filter(function (v) { return v && String(v).trim(); }).join(', ');
    }

    function fontCss(id) {
        var f = FONT_BY_ID[id];
        return (f ? '"' + f.css + '", ' : '') + 'Georgia, "Times New Roman", serif';
    }

    return {
        FONTS: FONTS, PALETTES: PALETTES, PRESETS: PRESETS, TYPES: TYPES,
        font: function (id) { return FONT_BY_ID[id] || null; },
        palette: function (id) { return PAL_BY_ID[id] || null; },
        preset: function (id) { return PRESET_BY_ID[id] || null; },
        fontCss: fontCss,
        makeDoc: makeDoc
    };
});
